import {template} from './template';
import {AoflElement, customElement} from '@aofl/element';
import {html} from 'lit-html';
import CDIV from '../cdiv-element';
import {routerInstance, ready as routerInstanceReady} from '../init-router-service';
import * as Sentry from '@sentry/browser';

/**
 *
 * @class RouteView
 * @extends {AoflElement}
 */
@customElement('route-view')
class RouteView extends AoflElement {
  /**
   *
   * Creates an instance of RouteView.
   */
  constructor() {
    super();
    this.template = template;
    this.renderedHtmlClasses = [];

    routerInstanceReady.then(() => {
      routerInstance.after(async (request, response, next) => {
        try {
          if (response.matchedRoute === null) {
            throw new Error('Route Not Found');
          }

          this.addRouteBreadcrumbs(response);

          const module = (await response.matchedRoute.resolve()).default;
          const isElementExport = typeof module.is !== 'undefined';
          const component = isElementExport ? module : await Promise.resolve(module());
          this.updateTemplate(response.matchedRoute.title, component.is,
            response?.matchedRoute?.meta?.customHTMLClasses);
        } catch (err) {
          Sentry.captureException(err);
          // Render 404 component
          const component = await CDIV;
          this.updateTemplate('404 - Not Found', component.is);
        } finally {
          next(response);
          await this.updateComplete;

          this.setLoaded();
        }
      });
    });
  }

  /**
   * @readonly
   */
  static is = 'route-view';

  /**
   *
   * @private
   * @param {String} title
   * @param {String} tag
   * @param {Array} customHTMLClasses
   */
  updateTemplate(title, tag, customHTMLClasses = []) {
    document.title = title;
    document.documentElement.id = tag;
    this.updateHtmlClasses(customHTMLClasses);
    const resolvedTemplate = `<${tag} id="page-component"></${tag}>`;
    this.template = () => html([resolvedTemplate]);
    this.requestUpdate();
  }

  /**
   *
   * @param {Object} resp
   */
  addRouteBreadcrumbs(resp) {
    Sentry.setTag('rotationId', resp?.matchedRoute?.rotationInfo?.version);

    return Sentry.addBreadcrumb({
      category: 'route.info',
      data: {
        from: resp?.from || 'start',
        to: resp?.to,
        rotationId: resp?.matchedRoute?.rotationInfo,
        rotationFolder: resp?.matchedRoute?.rotation,
        locale: resp?.matchedRoute?.locale
      },
      level: 'info'
    });
  }

  /**
   *
   */
  setLoaded() {
    window.scrollTo(0, 0);
    setTimeout(() => window.dispatchEvent(new CustomEvent('resize')), 2000);
    document.documentElement.classList.add('lazy-load');
  }

  /**
   *
   * @param {Array} htmlClasses
   */
  updateHtmlClasses(htmlClasses = []) {
    if (this.renderedHtmlClasses.length > 0) {
      document.documentElement.classList.remove(...this.renderedHtmlClasses);
    }

    if (htmlClasses.length > 0) {
      document.documentElement.classList.add(...htmlClasses);
    }

    this.renderedHtmlClasses = htmlClasses;
  }

  /**
   *
   *
   * @param {Array} args
   */
  connectedCallback(...args) {
    super.connectedCallback(...args);

    /* istanbul ignore next */
    while (this.firstChild) {
      this.removeChild(this.firstChild);
    }

    /* istanbul ignore else */
    if (typeof aofljsConfig.__prerender__ === 'undefined') {
      this.classList.add('loaded');
    }
  }

  /**
   *
   * @return {Object}
   */
  render() {
    return super.render(this.template, []);
  }
}

export default RouteView;
